import React, { FC, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useProjectUsers } from '../../../components/use-project-users/use-project-users';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import styles from '../../users/users-styles.module.sass';
import { DocumentTitle, ProjectName } from '@yakoffice/shared-components';
import { useParams } from 'react-router-dom';
import { UserProjectOverviewCard } from './cards/userProjectOverviewCard';
import Button from 'react-bootstrap/Button';
import { IUser } from '../../../model/user';
import Row from 'react-bootstrap/Row';
import { ProjectUserCard } from './cards/projectUserCard';
import Form from 'react-bootstrap/Form';
import { NewProjectUserCard } from './cards/newProjectUserCard';
import { NavigationRail } from '@yakoffice/navigation-rail';
import Alert from 'react-bootstrap/Alert';
import { CustomSpinner } from '@yakoffice/custom-spinner';
import { useAuthUser } from '@yakoffice/yakoffice-firebase';
import { InputGroup } from 'react-bootstrap';
import { useProjectThemeContext } from '@yakoffice/project-theme';

export const ProjectUsers: FC = observer(() => {

    const { projectId } = useParams<{ projectId: string }>();
    const projectTheme = useProjectThemeContext();

    const authUser = useAuthUser();
    const isAuthUserIamAdmin = authUser && authUser.yoUser?.iamRoles.find(projectIamRole => projectIamRole.projectId === projectId)?.roles.includes('admin');

    const [searchUser, setSearchUser] = useState<string>();
    const [activeUser, setActiveUser] = useState<IUser | null>();
    const [newUser, setNewUser] = useState<boolean>(false);
    const [callbackTrigger, setCallbackTrigger] = useState<number>(0);

    const loadUsersForProjectReResult = useProjectUsers(callbackTrigger);

    const renderUsers = (projectId: string) => {

        if (!loadUsersForProjectReResult.isLoaded)
            return <CustomSpinner spinnerText={'Loading project users...'} position="relative" />;

        if (loadUsersForProjectReResult.isLoaded && loadUsersForProjectReResult.users?.length === 0)
            return <Alert variant="info">No users found for this project</Alert>;

        const showUsers = searchUser == null ? loadUsersForProjectReResult.users : loadUsersForProjectReResult.users.filter(user => user.email.includes(searchUser.toLowerCase()));

        return showUsers.map(user => {

            const email = user.email;
            const hasProjectIamPermissions = user.iamRolesForProject(projectId);
            const hasBuildsClaim = user.hasToolClaim(projectId, 'builds');
            const hasPlayersClaim = user.hasToolClaim(projectId, 'players');
            const hasPublisherClaim = user.hasToolClaim(projectId, 'publisher');
            const hasSessionClaim = user.hasToolClaim(projectId, 'sessions');

            return <div onClick={() => {
                setNewUser(false);
                setActiveUser(user);
            }} key={user.id}><UserProjectOverviewCard email={email}
                                                      hasProjectIamPermissions={hasProjectIamPermissions}
                                                      hasBuildsClaim={hasBuildsClaim}
                                                      hasPlayersClaim={hasPlayersClaim}
                                                      hasPublisherClaim={hasPublisherClaim}
                                                      hasSessionClaim={hasSessionClaim}
                                                      active={activeUser === user} />
            </div>;
        });
    };

    const createNewUser = () => {
        setActiveUser(undefined);
        setNewUser(true);
    };

    DocumentTitle(`Users - Portal - ${ProjectName(projectId)}`);

    const updateCallbackTrigger = (clearActiveUser?: boolean) => {
        setCallbackTrigger(callbackTrigger + 1);
        clearActiveUser && setActiveUser(undefined);
    };

    const updateNewUserCallback = (clearActiveUser?: boolean) => {
        setCallbackTrigger(callbackTrigger + 1);
        clearActiveUser && setActiveUser(undefined);
        setNewUser(false);
    };

    return projectId == null ? null :
        <div
            className={`${styles.project} project ${projectTheme?.getColor()}`}>
            <NavigationRail toolId="portal" />

            <div className={`${styles.projectContent} projectContent themed-project`}>

                {loadUsersForProjectReResult.permissionDenied ?
                    <Alert variant="danger">You do not have IAM permissions for this project</Alert> :
                    <>
                        <Container fluid className={styles.stickyContainer}>
                            <div className={styles.usersTitle}>
                                <h1 className={styles.title}>Users</h1>
                                <div className={styles.searchForm}>
                                    <InputGroup>
                                        <Form.Control type="email" placeholder="Search by email address"
                                                      className={styles.userSearchField}
                                                      onChange={(e) => setSearchUser(e.target.value)}
                                                      disabled={!loadUsersForProjectReResult.isLoaded || loadUsersForProjectReResult.users?.length === 0}
                                        />
                                    </InputGroup>
                                </div>
                                <Col xs="auto">
                                    <Button className={styles.addUserButton} onClick={createNewUser}
                                            disabled={!isAuthUserIamAdmin}>
                                        <i className="fas fa-user-plus" /> New user
                                    </Button>
                                </Col>
                            </div>
                        </Container>

                        <Container fluid>
                            <Row>
                                <Col xl={4} lg={5} md={6} className={styles.scrollable}>
                                    {renderUsers(projectId)}
                                </Col>
                                <Col xl={8} lg={7} md={6} className={styles.scrollable}>
                                    <div className={styles.userCardWrapper}>
                                        <fieldset disabled={!isAuthUserIamAdmin}>
                                            {activeUser && <ProjectUserCard user={activeUser} projectId={projectId}
                                                                            updateCallbackTrigger={updateCallbackTrigger} />}
                                            {newUser && <NewProjectUserCard projectId={projectId}
                                                                            updateCallbackTrigger={updateNewUserCallback} />}
                                        </fieldset>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </>
                }
            </div>
        </div>;
});

